/**
 * Gastos previstos (puro): qué movimiento es el pago de cada previsto en cada año y cuánto suman al presupuesto.
 *
 * - Un previsto solo «explica» un gasto cuando está vinculado a su movimiento: confirmado a mano o reconocido solo
 *   (misma categoría, su mes o el de al lado, su palabra en el concepto e importe parecido). Que el total de la
 *   categoría cuadre no basta.
 * - «Lo normal» no cambia: solo deja de avisarse como raro lo que está vinculado a un previsto.
 * - Al presupuesto se suman los previstos marcados «Suma» en las categorías que ya tienen presupuesto ese año.
 */
import { round2, type BudgetRow, type EffectiveMovement } from '@pf/domain';
import type { PlannedRow } from '@pf/workbook';
import type { Computed } from './model.js';

/** Margen para reconocer un pago solo: ±35 % (o ±5 €); sin palabra, más estricto (±15 %). */
export const PLANNED_MATCH = { tolerance: 0.35, toleranceNoKeyword: 0.15, minEuros: 5, monthsAround: 1 };

export const appliesTo = (p: Pick<PlannedRow, 'repeat' | 'from' | 'to'>, year: number) =>
  p.repeat === 'once' ? p.from === year : year >= p.from && (p.to === null || year <= p.to);

const norm = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
const monthIndex = (iso: string) => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1;

export interface PlannedPayment { planned: PlannedRow; year: number; movements: EffectiveMovement[]; auto: boolean }
export interface PlannedIndex {
  /** Pagos reconocidos o confirmados, por previsto y año. */
  payments: PlannedPayment[];
  /** Movimiento → el previsto del que es pago. */
  byMovement: Map<string, PlannedRow>;
}

/** El pago de un previsto en un año: lo vinculado a mano (en su mes ±1) o, si no hay, lo que se reconozca solo. */
export function matchPlanned(planned: PlannedRow[], movements: EffectiveMovement[], years: number[]): PlannedIndex {
  const used = new Set<string>();
  const payments: PlannedPayment[] = [];
  const near = (p: PlannedRow, year: number, m: EffectiveMovement) =>
    !!m.date && Math.abs(monthIndex(m.date) - (year * 12 + p.month - 1)) <= PLANNED_MATCH.monthsAround;
  // Primero lo confirmado a mano (manda sobre lo automático), luego lo reconocido solo
  for (const pass of ['manual', 'auto'] as const) {
    for (const year of years) {
      for (const p of planned.filter(x => appliesTo(x, year))) {
        if (payments.some(x => x.planned.id === p.id && x.year === year)) continue;
        let found: EffectiveMovement[] = [];
        if (pass === 'manual') {
          found = p.linked.map(id => movements.find(m => m.id === id)).filter((m): m is EffectiveMovement => !!m && near(p, year, m) && !used.has(m.id));
        } else {
          const kw = norm(p.keyword.trim());
          const tol = Math.max(PLANNED_MATCH.minEuros, p.amount * (kw ? PLANNED_MATCH.tolerance : PLANNED_MATCH.toleranceNoKeyword));
          const best = movements
            .filter(m => m.id && !used.has(m.id) && !p.unlinked.includes(m.id) && m.amount < 0 && m.effCategory.toLowerCase() === p.category.toLowerCase()
              && near(p, year, m) && (!kw || norm(m.concept).includes(kw)) && Math.abs(Math.abs(m.amount) - p.amount) <= tol)
            .sort((a, b) => Math.abs(Math.abs(a.amount) - p.amount) - Math.abs(Math.abs(b.amount) - p.amount)
              || Math.abs(monthIndex(a.date) - (year * 12 + p.month - 1)) - Math.abs(monthIndex(b.date) - (year * 12 + p.month - 1)))[0];
          found = best ? [best] : [];
        }
        if (!found.length) continue;
        found.forEach(m => used.add(m.id));
        payments.push({ planned: p, year, movements: found, auto: pass === 'auto' });
      }
    }
  }
  const byMovement = new Map<string, PlannedRow>();
  payments.forEach(x => x.movements.forEach(m => byMovement.set(m.id, x.planned)));
  return { payments, byMovement };
}

const cache = new WeakMap<Computed, PlannedIndex>();
/** Pagos de los previstos con los datos del libro (se calcula una vez por sincronización). */
export function plannedIndex(c: Computed): PlannedIndex {
  const hit = cache.get(c);
  if (hit) return hit;
  const planned = c.wb.planned ?? [];
  const years = [...new Set(c.movements.map(m => Number(m.date.slice(0, 4))).filter(Boolean))].sort();
  const out = planned.length ? matchPlanned(planned, c.movements, years) : { payments: [], byMovement: new Map<string, PlannedRow>() };
  cache.set(c, out);
  return out;
}

/** Lo que una categoría tiene ya pagado de previstos en un mes (tu parte), y sus conceptos. */
export function plannedInMonth(c: Computed, category: string, month: string) {
  const idx = plannedIndex(c);
  const ms = c.movements.filter(m => m.effMonth === month && m.effCategory.toLowerCase() === category.toLowerCase() && idx.byMovement.has(m.id));
  return { amount: round2(-ms.reduce((t, m) => t + m.attributable, 0)) || 0, concepts: [...new Set(ms.map(m => idx.byMovement.get(m.id)!.concept || idx.byMovement.get(m.id)!.category))] };
}

/**
 * Presupuesto con los previstos «Suma»: se añaden al mes de su categoría en los años en que la categoría tiene
 * presupuesto (si no lo tiene, el previsto no inventa uno). Solo para mostrar y comparar: lo que se guarda es lo tuyo.
 */
export function budgetWithPlanned(budget: BudgetRow[], planned: PlannedRow[]): BudgetRow[] {
  const rows = budget.map(b => ({ ...b, overrides: [...b.overrides] }));
  for (const p of planned.filter(x => x.budget === 'add')) {
    for (const r of rows.filter(x => x.category.toLowerCase() === p.category.toLowerCase() && appliesTo(p, x.year))) {
      const i = p.month - 1;
      r.overrides[i] = round2((r.overrides[i] ?? r.monthly) + p.amount);
    }
  }
  return rows;
}

/** Lo que los previstos suman al presupuesto de una categoría en un mes (0 si no hay o ya estaban incluidos). */
export function plannedBudgetAdd(planned: PlannedRow[], budget: BudgetRow[], year: number, monthIndex0: number, category: string): number {
  if (!budget.some(b => b.year === year && b.category.toLowerCase() === category.toLowerCase())) return 0;
  return round2(planned.filter(p => p.budget === 'add' && p.month - 1 === monthIndex0 && p.category.toLowerCase() === category.toLowerCase() && appliesTo(p, year))
    .reduce((t, p) => t + p.amount, 0));
}
